import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader, FileText, Users, Hash, ArrowRight, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// Utility function to get color classes
const getColorClass = (color: string, type: string, variant: string) => {
  const colorMap: Record<string, Record<string, Record<string, string>>> = {
    purple: {
      bg: {
        '100': 'bg-blue-100',
        '900/30': 'bg-blue-900/30',
      },
      text: {
        '600': 'text-blue-600',
        '400': 'text-blue-400',
      }
    },
    blue: {
      bg: {
        '100': 'bg-blue-100',
        '900/30': 'bg-blue-900/30',
      },
      text: {
        '600': 'text-blue-600',
        '400': 'text-blue-400',
      }
    },
    teal: {
      bg: {
        '100': 'bg-teal-100',
        '900/30': 'bg-teal-900/30',
      },
      text: {
        '600': 'text-teal-600',
        '400': 'text-teal-400',
      }
    }
  };

  return colorMap[color]?.[type]?.[variant] || '';
};

const ResearchPage = () => {
  const [hasResults, setHasResults] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { id: projectId } = useParams();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const checkResults = async () => {
      if (!projectId) return;
      setIsLoading(true);

      try {
        const { data: crawl, error } = await supabase
          .from('website_crawls')
          .select('id')
          .eq('project_id', projectId)
          .eq('status', 'completed')
          .limit(1)
          .maybeSingle();

        if (error) {
          console.error('Error checking results:', error);
          throw error;
        }

        setHasResults(!!crawl);
      } catch (error) {
        console.error('Error checking results:', error);
        toast({
          title: "Error",
          description: "Failed to check research results",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    checkResults();
  }, [projectId, toast]);

  const researchSections = [
    {
      title: "Product Analysis",
      description: "View product description, features, pricing, and brand positioning",
      icon: FileText,
      path: "product",
      color: "purple"
    },
    {
      title: "Audience Analysis",
      description: "Explore target audience, pain points, desires, and social proof",
      icon: Users,
      path: "audience",
      color: "blue"
    },
    {
      title: "Keywords Analysis",
      description: "Discover core, competitive, niche, and long-tail keywords",
      icon: Hash,
      path: "keywords",
      color: "teal"
    }
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8 h-[calc(100vh-200px)]">
        <div className="animate-spin">
          <Loader className="h-8 w-8 text-primary" />
        </div>
      </div>
    );
  }

  if (!hasResults) {
    return (
      <div className="container mx-auto p-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-50 dark:from-blue-950/20 dark:to-blue-950/20 rounded-xl p-8 shadow-sm border border-blue-100 dark:border-blue-900/50">
          <div className="flex flex-col items-center justify-center text-center py-12 space-y-6">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-blue-100 dark:from-blue-900/30 dark:to-blue-900/30 rounded-full flex items-center justify-center shadow-inner">
              <Search className="h-10 w-10 text-blue-500" />
            </div>
            <div className="space-y-2 max-w-lg">
              <h2 className="text-2xl font-bold gradient-text">
                No Research Results Yet
              </h2>
              <p className="text-zinc-600 dark:text-zinc-400">
                To get insights about your product, audience, and keywords, you need to analyze a website first. This will help you understand your market better.
              </p>
            </div>
            <Button 
              onClick={() => navigate(`/projects/${projectId}/analyze`)}
              className="bg-gradient-to-r from-blue-600 to-sky-500 text-white hover:opacity-90 transition-all duration-300 shadow-md hover:shadow-lg"
              size="lg"
            >
              Analyze Website
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 animate-fadeIn">
      <div className="bg-gradient-to-br from-blue-50 to-blue-50 dark:from-blue-950/20 dark:to-blue-950/20 rounded-xl p-8 shadow-sm border border-blue-100 dark:border-blue-900/50">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold gradient-text mb-3">
            Research Results
          </h1>
          <p className="text-zinc-600 dark:text-zinc-400 max-w-2xl mx-auto">
            Your website analysis is complete. Explore the different sections below to view detailed insights about your product, audience, and keywords.
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-6">
          {researchSections.map((section) => {
            const Icon = section.icon;
            return (
              <div 
                key={section.title} 
                className="bg-white dark:bg-zinc-900 rounded-xl shadow-sm border border-zinc-200 dark:border-zinc-800 hover:shadow-md transition-all duration-200 cursor-pointer group overflow-hidden"
                onClick={() => navigate(`/projects/${projectId}/research/${section.path}`)}
              >
                <div className="p-6 relative">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-blue-100/50 to-transparent dark:from-blue-900/10 rounded-bl-full -z-10"></div>
                  
                  <div className={cn(
                    "p-3 rounded-full w-fit mb-4",
                    getColorClass(section.color, "bg", "100"),
                    `dark:${getColorClass(section.color, "bg", "900/30")}`
                  )}>
                    <Icon className={cn(
                      "h-6 w-6",
                      getColorClass(section.color, "text", "600"),
                      `dark:${getColorClass(section.color, "text", "400")}`
                    )} />
                  </div>
                  
                  <h3 className="text-lg font-semibold mb-2 flex items-center gap-2 text-zinc-900 dark:text-zinc-100">
                    {section.title}
                    <ArrowRight className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-all group-hover:translate-x-1 text-blue-500" />
                  </h3>
                  
                  <p className="text-zinc-600 dark:text-zinc-400 text-sm">{section.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ResearchPage;